import {
  BMComponentsType,
  BMDivider,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcPassengerAdult16 from "@traveloka/icon-kit-web/react/IcPassengerAdult16";
import IcPassengerInfantBaby16 from "@traveloka/icon-kit-web/react/IcPassengerInfantBaby16";
import IcPassengerKid16 from "@traveloka/icon-kit-web/react/IcPassengerKid16";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useTravelerPickerContextV2 } from "../../../../providers/TravelerPickerContextV2";
import { IconKitProps } from "../../../../types";
import { formatMessage } from "../../../../utils";
import { FlightNonEmployee, FormValues, HotelNonEmployee } from "./types";

type FormContext = FormValues<Partial<FlightNonEmployee & HotelNonEmployee>>;

type Props = {
  onOpenNonEmployeePicker(): void;
};

export default function TravelerPickerSelectNonEmployeeContent({
  onOpenNonEmployeePicker,
}: Props) {
  const { type } = useTravelerPickerContextV2();
  const { CorporateTravelerPicker: cr } = useMobileContentResource();
  const { control } = useFormContext<FormContext>();
  const nonEmployee = useWatch({ control, name: "nonEmployee" });

  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  if (
    !nonEmployee ||
    (nonEmployee.adults ?? 0) +
      (nonEmployee.children ?? 0) +
      (nonEmployee.infants ?? 0) +
      (nonEmployee.childrenAges?.length ?? 0) ===
      0
  ) {
    return (
      <BMView style={styles.container} spacing="lg">
        <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
          {formatMessage(cr.selectedNonEmployeeSectionTitle, { type })}
        </BMText>
        <BMView style={[styles.innerWrapper, styles.padding]} spacing="xs">
          <BMText variant="paragraphNormal" ink="BMStyleTextInkSecondary">
            {cr.emptyNonEmployeeInfoText}
          </BMText>
          <BMText
            testID="add_nonemployee-button"
            ink="BMStyleTextInkAction"
            onPress={onOpenNonEmployeePicker}
          >
            {formatMessage(cr.addNonEmployeeButtonText, { type })}
          </BMText>
        </BMView>
      </BMView>
    );
  }

  return (
    <BMView style={styles.container} spacing="lg">
      <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
        {formatMessage(cr.selectedNonEmployeeSectionTitle, { type })}
      </BMText>
      <BMView style={styles.innerWrapper}>
        {type === "flight" && (
          <BMView row>
            <NonEmployeeItem
              Icon={IcPassengerAdult16}
              content={formatMessage(cr.adultUnit, {
                num: nonEmployee.adults || "none",
              })}
            />
            <BMDivider orientation="VERTICAL" type="SUBTLE" />
            <NonEmployeeItem
              Icon={IcPassengerKid16}
              content={formatMessage(cr.childUnit, {
                num: nonEmployee.children || "none",
              })}
            />
            <BMDivider orientation="VERTICAL" type="SUBTLE" />
            <NonEmployeeItem
              Icon={IcPassengerInfantBaby16}
              content={formatMessage(cr.infantUnit, {
                num: nonEmployee.infants || "none",
              })}
            />
          </BMView>
        )}
        {type === "hotel" && (
          <BMView row>
            <NonEmployeeItem
              Icon={IcPassengerAdult16}
              content={formatMessage(cr.adultUnit, {
                num: nonEmployee.adults || "none",
              })}
            />
            <BMDivider orientation="VERTICAL" type="SUBTLE" />
            <NonEmployeeItem
              Icon={IcPassengerKid16}
              content={formatMessage(cr.childUnit, {
                num: nonEmployee.childrenAges?.length || "none",
              })}
            />
          </BMView>
        )}
        <BMDivider type="SUBTLE" />
        <BMView style={styles.padding}>
          <BMText ink="BMStyleTextInkAction" onPress={onOpenNonEmployeePicker}>
            {formatMessage(cr.addNonEmployeeButtonText, { type })}
          </BMText>
        </BMView>
      </BMView>
    </BMView>
  );
}

type NonEmployeeItemProps = {
  Icon: FC<IconKitProps>;
  content: string;
};
function NonEmployeeItem({ Icon, content }: NonEmployeeItemProps) {
  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  return (
    <BMView
      style={styles.padding}
      flex
      row
      spacing="xs"
      align="center"
      justify="center"
    >
      <Icon width={16} height={16} color={BMComponents.BMStyleIconInkPrimary} />
      <BMText>{content}</BMText>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      paddingHorizontal: BMPrimitives.BMSize.md,
      paddingTop: BMPrimitives.BMSize.lg,
      paddingBottom: BMPrimitives.BMSize.xl3,
    },
    innerWrapper: {
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
    },
    padding: {
      padding: BMPrimitives.BMSize.md,
    },
  });
}
